'use client';
import { useState } from 'react';
import useMeasure from 'react-use-measure';
import { motion } from 'framer-motion';
import { FAQPage, WithContext } from 'schema-dts';

export type FaqEntry = {
  title: string;
  content: string;
};

const faqs: FaqEntry[] = [
  {
    title: "What's a query?",
    content:
      'A query is a single piece of content that you send to the API for analysis. It can be a chat message, email, document, tweet, or any other type of text. Images count for 3 queries.',
  },
  {
    title: 'What if I need more queries than my plan allows?',
    content:
      'You can opt in to pay-as-you-go pricing and pay for additional queries, or upgrade your plan. If our default plans do not fit your needs, you can request a custom plan that fits your needs better.',
  },
  {
    title: 'What happens if I exceed the quota?',
    content:
      'You can opt in for pay-as-you-go pricing to pay for additional queries. Another option is to upgrade or request a custom plan that fits your usage needs better. If you do not opt in for pay-as-you-go pricing, the API will return an error until the next billing cycle.',
  },

  {
    title: 'What are AI moderation agents?',
    content:
      'AI moderation agents are AI agents that you can use to moderate your content. They are can be instructed to moderate content in a specific way using your own guidelines.',
  },
  {
    title: 'What are custom models?',
    content:
      'Custom models are models that you train to your specific needs. If our default models do not fit your needs, you can train a custom model that performs better for your use case.',
  },
  {
    title: 'How do I train a custom model?',
    content:
      'Custom models need examples of your given use case. You can upload examples as a CSV or Excel file, or you can use our web interface to create and label examples. We recommend a minimum of 100 examples per label, but you can start smaller and add more examples as you go to continuously improve your model.',
  },

  {
    title: 'Is there a discount at larger volumes?',
    content:
      'Yes, when discussing a custom plan we can offer discounts depending on your which model you need and the type of queries you send.',
  },
  {
    title: 'How does the free trial work?',
    content:
      'You can sign up for a free trial and get 30 days for free on any plan.',
  },
];

export default function Faq({
  values = faqs,
  description = "Looking for more info? Here are some things we're commonly asked.",
  startOpen = false,
}) {
  const jsonLd: WithContext<FAQPage> = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: values.map((faq, idx) => ({
      '@type': 'Question',
      name: faq.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.content,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="px-4">
        <div className="max-w-3xl mx-auto text-gray-600 prose  prose-lg md:prose-xl  ">
          <div className=" mx-auto mb-12 text-center ">
            <h2
              style={{
                marginBottom: '0.3em',
              }}
            >
              Frequently asked questions
            </h2>
            {!!description && <p>{description}</p>}
          </div>
          <div className=" mx-auto ">
            <div className="flex flex-wrap -m-1 ">
              {values.map((faq, idx) => (
                <Collapsible key={idx} title={faq.title} startOpen={startOpen}>
                  <span dangerouslySetInnerHTML={{ __html: faq.content }} />
                </Collapsible>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Collapsible = ({ children, title, startOpen }) => {
  const [isOpen, setIsOpen] = useState(!!startOpen);

  const [measureRef, { height }] = useMeasure();
  const [measureRefTitle, { height: heightTitle }] = useMeasure();

  return (
    <div className="w-full p-1">
      <motion.div
        className={
          'py-0 px-8 bg-white bg-opacity-60 border-2  rounded-2xl shadow-10xl cursor-pointer ' +
          (isOpen && !startOpen
            ? ' border-indigo-600 '
            : ' border-gray-200 hover:border-gray-300 ')
        }
        initial={{
          height: heightTitle || 85,
        }}
        animate={{
          height: isOpen ? height + 24 : heightTitle || 85,
        }}
        style={{
          overflow: 'hidden',
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap justify-between -m-2 select-none">
          <div className="flex-1 p-2" ref={measureRef}>
            <h4
              ref={measureRefTitle}
              className="font-semibold leading-normal text-black "
              style={{
                marginTop: 0,
                marginBottom: 0,
                paddingTop: 24,
                paddingBottom: 24,
              }}
            >
              {title}
            </h4>

            <div className="prose">
              <p
                className="whitespace-pre-line"
                style={{
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                {children}
              </p>
            </div>
          </div>
          <div className="w-auto p-2 mt-6">
            <svg
              className={
                'relative top-1 transition-transform duration-100 ' +
                (!isOpen ? 'rotate-180' : '')
              }
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              data-config-id="auto-svg-1-1"
            >
              <path
                d="M4.16732 12.5L10.0007 6.66667L15.834 12.5"
                stroke={isOpen ? '#4F46E5' : 'black'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

<div className="py-7 px-8 bg-white bg-opacity-60 border border-gray-200 hover:border-gray-300 rounded-2xl shadow-10xl">
  <div className="flex flex-wrap justify-between -m-2">
    <div className="flex-1 p-2">
      <h3
        className="text-lg font-semibold leading-normal"
        data-config-id="auto-txt-5-1"
      >
        How to claim your 25% discount offer?
      </h3>
    </div>
    <div className="w-auto p-2">
      <svg
        className="relative top-1"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        data-config-id="auto-svg-2-1"
      >
        <path
          d="M14.25 6.75L9 12L3.75 6.75"
          stroke="#18181B"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
      </svg>
    </div>
  </div>
</div>;
